// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback } from 'react';
import type { StepComponentProps } from '../../types/wizard';
import type { BenefitCardEntry } from '../../types/providerAccess';
import { FileUploadField } from '../shared/FileUploadField';
import { toIsoDate } from '../../utils/date';

const YES_NO_OPTIONS = [
  { value: 'Yes', text: 'Yes' },
  { value: 'No', text: 'No' },
];

const LIFE_COVER_TYPES = [
  { value: 'Death in Service', text: 'Death in Service' },
  { value: 'Executive Term Cover', text: 'Executive Term Cover' },
  { value: 'Term Life Insurance', text: 'Term Life Insurance' },
  { value: 'Whole of Life', text: 'Whole of Life' },
  { value: 'Other', text: 'Other' },
];

const INCOME_PROTECTION_TYPES = [
  { value: 'Permanent Health Insurance', text: 'Permanent Health Insurance' },
  { value: 'Executive Income Protection', text: 'Executive Income Protection' },
  { value: 'Group Income Protection', text: 'Group Income Protection' },
  { value: 'Other', text: 'Other' },
];

const SPECIFIED_ILLNESS_TYPES = [
  { value: 'Specified Illness Cover', text: 'Specified Illness Cover' },
  { value: 'Critical Illness Cover', text: 'Critical Illness Cover' },
  { value: 'Other', text: 'Other' },
];

type BenefitKey = 'lifeCover' | 'incomeProtection' | 'specifiedIllness';

const BENEFIT_CONFIG: { key: BenefitKey; enabledKey: string; cardsKey: string; label: string; typeOptions: { value: string; text: string }[]; salaryLabel: string; secondFieldLabel: string }[] = [
  { key: 'lifeCover', enabledKey: 'lifeCoverEnabled', cardsKey: 'lifeCoverCards', label: 'Life Cover', typeOptions: LIFE_COVER_TYPES, salaryLabel: 'Salary on record', secondFieldLabel: 'Times salary' },
  { key: 'incomeProtection', enabledKey: 'incomeProtectionEnabled', cardsKey: 'incomeProtectionCards', label: 'Income Protection', typeOptions: INCOME_PROTECTION_TYPES, salaryLabel: 'Salary on record for cover', secondFieldLabel: '% of salary' },
  { key: 'specifiedIllness', enabledKey: 'specifiedIllnessEnabled', cardsKey: 'specifiedIllnessCards', label: 'Specified Illness', typeOptions: SPECIFIED_ILLNESS_TYPES, salaryLabel: 'Salary on record', secondFieldLabel: 'Times salary' },
];

/** Strip everything except digits and dot, keep only first dot */
function toRawNumber(display: string): string {
  const stripped = display.replace(/[^0-9.]/g, '');
  const parts = stripped.split('.');
  if (parts.length <= 1) return stripped;
  return parts[0] + '.' + parts.slice(1).join('');
}

function formatEuro(raw: string): string {
  if (!raw) return '';
  const parts = raw.split('.');
  const intPart = parts[0].replace(/^0+(?=\d)/, '');
  const formatted = (intPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const dec = parts.length > 1 ? '.' + parts[1] : '';
  return `${formatted}${dec}`;
}

function formatEuroBlur(raw: string): string {
  if (!raw) return '';
  const num = parseFloat(raw);
  if (isNaN(num)) return '';
  return num.toFixed(2);
}

// Chevron SVG
const chevronIcon = (
  <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1.5L6 6.5L11 1.5" stroke="#525252" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Collapse/expand chevron
const collapseIcon = (expanded: boolean) => (
  <svg className={`transition-transform ${expanded ? '' : 'rotate-180'}`} width="16" height="10" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 6.5L6 1.5L11 6.5" stroke="#141414" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Trash icon
const trashIcon = (id: string) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath={`url(#clip_trash_ab_${id})`}>
      <mask id={`mask_trash_ab_${id}`} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
        <path d="M15.5 0.5V15.5H0.5V0.5H15.5Z" fill="white" stroke="white"/>
      </mask>
      <g mask={`url(#mask_trash_ab_${id})`}>
        <path d="M14.0004 4.4873C13.9871 4.4873 13.9671 4.4873 13.9471 4.4873C10.4204 4.13397 6.90042 4.00064 3.41377 4.35397L2.05377 4.4873C1.77377 4.51397 1.5271 4.31397 1.50043 4.03397C1.47377 3.75397 1.67377 3.51397 1.9471 3.4873L3.3071 3.35397C6.85375 2.99397 10.4471 3.13397 14.0471 3.4873C14.3204 3.51397 14.5204 3.76064 14.4938 4.03397C14.4738 4.29397 14.2538 4.4873 14.0004 4.4873Z" fill="#2D0A6C"/>
        <path d="M5.66749 3.8135C5.64082 3.8135 5.61415 3.8135 5.58082 3.80683C5.31415 3.76016 5.12749 3.50016 5.17415 3.2335L5.32082 2.36016C5.42749 1.72016 5.57415 0.833496 7.12748 0.833496H8.87415C10.4341 0.833496 10.5808 1.7535 10.6808 2.36683L10.8275 3.2335C10.8741 3.50683 10.6875 3.76683 10.4208 3.80683C10.1475 3.8535 9.88748 3.66683 9.84748 3.40016L9.70081 2.5335C9.60748 1.9535 9.58748 1.84016 8.88081 1.84016H7.13415C6.42749 1.84016 6.41415 1.9335 6.31415 2.52683L6.16082 3.3935C6.12082 3.64016 5.90749 3.8135 5.66749 3.8135Z" fill="#2D0A6C"/>
        <path d="M10.1406 15.1667H5.86058C3.53392 15.1667 3.44058 13.8801 3.36725 12.8401L2.93392 6.12672C2.91392 5.85338 3.12725 5.61338 3.40058 5.59338C3.68058 5.58005 3.91392 5.78672 3.93392 6.06005L4.36725 12.7734C4.44058 13.7867 4.46725 14.1667 5.86058 14.1667H10.1406C11.5406 14.1667 11.5672 13.7867 11.6339 12.7734L12.0672 6.06005C12.0872 5.78672 12.3273 5.58005 12.6006 5.59338C12.8739 5.61338 13.0872 5.84672 13.0672 6.12672L12.6339 12.8401C12.5606 13.8801 12.4672 15.1667 10.1406 15.1667Z" fill="#2D0A6C"/>
        <path d="M9.10673 11.5H6.88673C6.61339 11.5 6.38672 11.2733 6.38672 11C6.38672 10.7267 6.61339 10.5 6.88673 10.5H9.10673C9.38007 10.5 9.60673 10.7267 9.60673 11C9.60673 11.2733 9.38007 11.5 9.10673 11.5Z" fill="#141414"/>
        <path d="M9.66732 8.8335H6.33398C6.06065 8.8335 5.83398 8.60683 5.83398 8.3335C5.83398 8.06016 6.06065 7.8335 6.33398 7.8335H9.66732C9.94065 7.8335 10.1673 8.06016 10.1673 8.3335C10.1673 8.60683 9.94065 8.8335 9.66732 8.8335Z" fill="#141414"/>
      </g>
    </g>
    <defs>
      <clipPath id={`clip_trash_ab_${id}`}>
        <rect width="16" height="16" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

// Add icon
const addIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip_add_ab)">
      <mask id="mask_add_ab" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
        <path d="M15.5 0.5V15.5H0.5V0.5H15.5Z" fill="white" stroke="white"/>
      </mask>
      <g mask="url(#mask_add_ab)">
        <path d="M10.6663 8.5H5.33301C5.05967 8.5 4.83301 8.27333 4.83301 8C4.83301 7.72667 5.05967 7.5 5.33301 7.5H10.6663C10.9397 7.5 11.1663 7.72667 11.1663 8C11.1663 8.27333 10.9397 8.5 10.6663 8.5Z" fill="#2D0A6C"/>
        <path d="M8 11.1668C7.72667 11.1668 7.5 10.9402 7.5 10.6668V5.3335C7.5 5.06016 7.72667 4.8335 8 4.8335C8.27333 4.8335 8.5 5.06016 8.5 5.3335V10.6668C8.5 10.9402 8.27333 11.1668 8 11.1668Z" fill="#2D0A6C"/>
        <path d="M9.99967 15.1668H5.99967C2.37967 15.1668 0.833008 13.6202 0.833008 10.0002V6.00016C0.833008 2.38016 2.37967 0.833496 5.99967 0.833496H9.99967C13.6197 0.833496 15.1663 2.38016 15.1663 6.00016V10.0002C15.1663 13.6202 13.6197 15.1668 9.99967 15.1668ZM5.99967 1.8335C2.92634 1.8335 1.83301 2.92683 1.83301 6.00016V10.0002C1.83301 13.0735 2.92634 14.1668 5.99967 14.1668H9.99967C13.073 14.1668 14.1663 13.0735 14.1663 10.0002V6.00016C14.1663 2.92683 13.073 1.8335 9.99967 1.8335H5.99967Z" fill="#2D0A6C"/>
      </g>
    </g>
    <defs>
      <clipPath id="clip_add_ab">
        <rect width="16" height="16" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

// Checkbox SVG
const checkboxIcon = (checked: boolean) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="cursor-pointer flex-shrink-0">
    {checked ? (
      <>
        <rect width="20" height="20" rx="4" fill="#2D0A6C"/>
        <path d="M5.5 10L8.5 13L14.5 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </>
    ) : (
      <rect x="0.5" y="0.5" width="19" height="19" rx="3.5" fill="white" stroke="#525252"/>
    )}
  </svg>
);

const EMPTY_CARD: BenefitCardEntry = {};

export function StepAssociatedBenefits({
  data,
  onFieldChange,
  pendingFiles,
  onAddFile,
  onRemoveFile,
}: StepComponentProps) {
  const hasProtection = (data.hasProtectionBenefits as string) === 'Yes';

  const lifeCoverEnabled = !!data.lifeCoverEnabled;
  const incomeProtectionEnabled = !!data.incomeProtectionEnabled;
  const specifiedIllnessEnabled = !!data.specifiedIllnessEnabled;

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggleCollapse = (id: string) => {
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getCards = useCallback((cardsKey: string): BenefitCardEntry[] => {
    return (data[cardsKey] as BenefitCardEntry[]) || [];
  }, [data]);

  const updateCard = useCallback((cardsKey: string, index: number, field: keyof BenefitCardEntry, value: string) => {
    const cards = [...getCards(cardsKey)];
    cards[index] = { ...cards[index], [field]: value };
    onFieldChange(cardsKey, cards);
  }, [getCards, onFieldChange]);

  const addCard = useCallback((cardsKey: string) => {
    onFieldChange(cardsKey, [...getCards(cardsKey), { ...EMPTY_CARD }]);
  }, [getCards, onFieldChange]);

  const removeCard = useCallback((cardsKey: string, index: number) => {
    const updated = getCards(cardsKey).filter((_, i) => i !== index);
    onFieldChange(cardsKey, updated);
  }, [getCards, onFieldChange]);

  const handleCurrencyChange = (cardsKey: string, index: number, field: keyof BenefitCardEntry, inputValue: string) => {
    const raw = toRawNumber(inputValue);
    if (raw && !/^\d*\.?\d*$/.test(raw)) return;
    updateCard(cardsKey, index, field, raw);
  };

  const handleCurrencyBlur = (cardsKey: string, index: number, field: keyof BenefitCardEntry) => {
    const cards = getCards(cardsKey);
    const raw = (cards[index]?.[field] as string) || '';
    if (raw) updateCard(cardsKey, index, field, formatEuroBlur(raw));
  };

  // Auto-add first card when checkbox is enabled
  const handleCheckboxToggle = useCallback((enabledKey: string, cardsKey: string, checked: boolean) => {
    onFieldChange(enabledKey, checked);
    if (checked && getCards(cardsKey).length === 0) {
      onFieldChange(cardsKey, [{ ...EMPTY_CARD }]);
    }
    if (!checked) {
      onFieldChange(cardsKey, []);
    }
  }, [onFieldChange, getCards]);

  const inputClass =
    "w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white text-[#333] font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary";

  const selectClass = (hasValue: boolean) =>
    `w-full h-[48px] px-md pr-xxl rounded-lg border border-grey-04 bg-white ${hasValue ? "text-[#333]" : "text-[#B6B6B6]"} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary appearance-none cursor-pointer`;

  const currencyWrapper =
    "flex items-center w-full h-[48px] px-md rounded-lg border border-grey-04 bg-white focus-within:border-purple-primary";

  const labelClass = "text-black font-sans text-body-02 font-medium leading-normal not-italic";

  const dateInputClass = (hasValue: boolean) =>
    `w-full h-[48px] px-md pr-2xl rounded-lg border border-grey-04 bg-white ${hasValue ? 'text-[#333]' : 'text-grey-04'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;

  const calendarIcon = (maskId: string) => (
    <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id={maskId} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
        <path d="M16 0H0V16H16V0Z" fill="white"/>
      </mask>
      <g mask={`url(#${maskId})`}>
        <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
        <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
        <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
        <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
      </g>
    </svg>
  );

  const renderTextField = (cardsKey: string, idx: number, field: string, label: string, required = true) => (
    <div className="flex flex-col gap-sm flex-1">
      <div className={labelClass}>{label}{required && <> <span className="text-[#E7000B]">*</span></>}</div>
      <input type="text" value={getCards(cardsKey)[idx]?.[field] || ''} onChange={(e) => updateCard(cardsKey, idx, field, e.target.value)} className={inputClass} />
    </div>
  );

  const renderCurrencyCardField = (cardsKey: string, idx: number, field: string, label: string, required = true) => (
    <div className="flex flex-col gap-sm flex-1">
      <div className={labelClass}>{label}{required && <> <span className="text-[#E7000B]">*</span></>}</div>
      <div className={currencyWrapper}>
        <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
        <input type="text" inputMode="decimal" value={getCards(cardsKey)[idx]?.[field] ? formatEuro(getCards(cardsKey)[idx][field] || '') : ''} onChange={(e) => handleCurrencyChange(cardsKey, idx, field, e.target.value)} onBlur={() => handleCurrencyBlur(cardsKey, idx, field)} className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none" />
      </div>
    </div>
  );

  const renderDateCardField = (cardsKey: string, idx: number, field: string, label: string, maskId: string) => (
    <div className="flex flex-col gap-sm flex-1">
      <div className={labelClass}>{label} <span className="text-[#E7000B]">*</span></div>
      <div className="relative">
        <input type="date" value={getCards(cardsKey)[idx]?.[field] || ''} onChange={(e) => updateCard(cardsKey, idx, field, toIsoDate(e.target.value))} className={dateInputClass(!!getCards(cardsKey)[idx]?.[field])} />
        {calendarIcon(maskId)}
      </div>
    </div>
  );

  const renderSpecifiedIllnessBody = (_card: BenefitCardEntry, index: number, cardsKey: string) => (
    <div className="flex flex-col gap-md px-lg pb-[20px]">
      <div className="flex flex-row gap-lg">
        {renderTextField(cardsKey, index, 'nameCovered', 'Name of Life Covered')}
        {renderTextField(cardsKey, index, 'policyRefNumber', 'Policy Reference Number')}
      </div>
      <div className="flex flex-row gap-lg">
        {renderCurrencyCardField(cardsKey, index, 'specifiedIllnessCover', 'Specified Illness Cover')}
        {renderTextField(cardsKey, index, 'termOfCover', 'Term of Cover')}
      </div>
      <div className="flex flex-row gap-lg">
        {renderDateCardField(cardsKey, index, 'startDateOfCover', 'Start date of cover', `mask_si_start_${index}`)}
        {renderDateCardField(cardsKey, index, 'endDateOfCover', 'End date of cover', `mask_si_end_${index}`)}
      </div>
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className={labelClass}>Cover status <span className="text-[#E7000B]">*</span></div>
          <div className="relative">
            <select
              value={getCards(cardsKey)[index]?.coverInForce || ''}
              onChange={(e) => updateCard(cardsKey, index, 'coverInForce', e.target.value)}
              className={selectClass(!!getCards(cardsKey)[index]?.coverInForce)}
            >
              <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
              <option value="In force" className="text-[#333]">In force</option>
              <option value="Not active" className="text-[#333]">Not active</option>
            </select>
            {chevronIcon}
          </div>
        </div>
        {renderTextField(cardsKey, index, 'connectedEmployerName', 'Connected employer name', false)}
      </div>
      <div className="flex flex-row gap-lg">
        {renderTextField(cardsKey, index, 'nraCover', 'NRA of cover')}
        {renderCurrencyCardField(cardsKey, index, 'monthlyInsurancePremium', 'Monthly insurance premium', false)}
      </div>
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className={labelClass}>Does the policy have loading conditions <span className="text-[#E7000B]">*</span></div>
          <div className="relative">
            <select
              value={getCards(cardsKey)[index]?.specifiedIllnessExclusions || ''}
              onChange={(e) => updateCard(cardsKey, index, 'specifiedIllnessExclusions', e.target.value)}
              className={selectClass(!!getCards(cardsKey)[index]?.specifiedIllnessExclusions)}
            >
              <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
              <option value="Yes" className="text-[#333]">Yes</option>
              <option value="No" className="text-[#333]">No</option>
            </select>
            {chevronIcon}
          </div>
        </div>
        {renderTextField(cardsKey, index, 'continuationOptionDetails', 'Continuation option details')}
      </div>
      <div className="flex flex-row gap-lg">
        {renderTextField(cardsKey, index, 'whoPaysThePremium', 'Who pays the premium?')}
        {renderTextField(cardsKey, index, 'indexLinked', 'Index linked?')}
      </div>

      <FileUploadField
        fieldKey={`specifiedIllnessDoc_${index}`}
        label="Specified Illness Policy Doc"
        files={pendingFiles?.get(`specifiedIllnessDoc_${index}`)}
        onAddFile={onAddFile}
        onRemoveFile={onRemoveFile}
      />

      <button type="button" onClick={() => removeCard(cardsKey, index)} className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit">
        {trashIcon(`specifiedIllness_${index}`)}
        <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">Remove Specified Illness</div>
      </button>
    </div>
  );

  const renderDefaultBody = (config: typeof BENEFIT_CONFIG[number], card: BenefitCardEntry, index: number) => {
    const isLifeCover = config.key === 'lifeCover';
    const isIncomeProtection = config.key === 'incomeProtection';
    const typeLabel = isLifeCover ? 'Type of Life Cover' : 'Type of Cover';
    const showContinuationOption = (isLifeCover && card.typeCover === 'Death in Service') || (isIncomeProtection && card.typeCover === 'Executive Income Protection');
    return (
      <div className="flex flex-col gap-md px-lg pb-[20px]">
        {/* Type of Cover | Name of Life Covered */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>{typeLabel} <span className="text-[#E7000B]">*</span></div>
            <div className="relative">
              <select value={card.typeCover || ''} onChange={(e) => updateCard(config.cardsKey, index, 'typeCover', e.target.value)} className={selectClass(!!card.typeCover)}>
                <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
                {config.typeOptions.map((o) => (<option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>))}
              </select>
              {chevronIcon}
            </div>
          </div>
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>Name of Life Covered <span className="text-[#E7000B]">*</span></div>
            <input type="text" value={card.nameCovered || ''} onChange={(e) => updateCard(config.cardsKey, index, 'nameCovered', e.target.value)} className={inputClass} />
          </div>
        </div>

        {/* Policy Reference Number | Salary on record */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>Policy Reference Number <span className="text-[#E7000B]">*</span></div>
            <input type="text" value={card.policyRefNumber || ''} onChange={(e) => updateCard(config.cardsKey, index, 'policyRefNumber', e.target.value)} className={inputClass} />
          </div>
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>{config.salaryLabel} <span className="text-[#E7000B]">*</span></div>
            {isLifeCover ? (
              <div className={currencyWrapper}>
                <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
                <input type="text" inputMode="decimal" value={card.salaryOnRecord ? formatEuro(card.salaryOnRecord) : ''} onChange={(e) => handleCurrencyChange(config.cardsKey, index, 'salaryOnRecord', e.target.value)} onBlur={() => handleCurrencyBlur(config.cardsKey, index, 'salaryOnRecord')} className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none" />
              </div>
            ) : (
              <input type="text" value={card.salaryOnRecord || ''} onChange={(e) => updateCard(config.cardsKey, index, 'salaryOnRecord', e.target.value)} className={inputClass} />
            )}
          </div>
        </div>

        {/* NRA of cover | Times salary OR % of salary */}
        <div className="flex flex-row gap-lg">
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>NRA of cover <span className="text-[#E7000B]">*</span></div>
            <input type="text" value={card.nraCover || ''} onChange={(e) => updateCard(config.cardsKey, index, 'nraCover', e.target.value)} className={inputClass} />
          </div>
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>{config.secondFieldLabel} <span className="text-[#E7000B]">*</span></div>
            {isLifeCover ? (
              <input type="text" value={card.timesSalary || ''} onChange={(e) => updateCard(config.cardsKey, index, 'timesSalary', e.target.value)} className={inputClass} />
            ) : (
              <input type="text" value={card.percentOfSalary || ''} onChange={(e) => updateCard(config.cardsKey, index, 'percentOfSalary', e.target.value)} className={inputClass} />
            )}
          </div>
        </div>

        {/* Is the premium being paid using units from a pension policy? | Continuation option details (Death in Service only) */}
        <div className="flex flex-row gap-lg items-end">
          <div className="flex flex-col gap-sm flex-1">
            <div className={labelClass}>Is the premium being paid using units from a pension policy?<span className="text-[#E7000B]"> *</span></div>
            <div className="relative">
              <select value={card.premiumFromPension || ''} onChange={(e) => updateCard(config.cardsKey, index, 'premiumFromPension', e.target.value)} className={selectClass(!!card.premiumFromPension)}>
                <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
                {YES_NO_OPTIONS.map((o) => (<option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>))}
              </select>
              {chevronIcon}
            </div>
          </div>
          {showContinuationOption ? (
            <div className="flex flex-col gap-sm flex-1">
              <div className={labelClass}>Continuation option details <span className="text-[#E7000B]">*</span></div>
              <input type="text" value={card.continuationOptionDetails || ''} onChange={(e) => updateCard(config.cardsKey, index, 'continuationOptionDetails', e.target.value)} className={inputClass} />
            </div>
          ) : (
            <div className="flex-1" />
          )}
        </div>

        {/* Remove button */}
        <button type="button" onClick={() => removeCard(config.cardsKey, index)} className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit">
          {trashIcon(`${config.key}_${index}`)}
          <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">Remove {config.label}</div>
        </button>
      </div>
    );
  };

  const renderBenefitCards = (config: typeof BENEFIT_CONFIG[number]) => {
    const cards = getCards(config.cardsKey);
    const isSpecifiedIllness = config.key === 'specifiedIllness';

    return (
      <div className="flex flex-col gap-md">
        {cards.map((card, index) => {
          const cardId = `${config.key}_${index}`;
          const isCollapsed = collapsed[cardId];

          return (
            <div key={index} className="flex flex-col bg-lilac-06 rounded-button overflow-hidden">
              {/* Card header */}
              <button
                type="button"
                onClick={() => toggleCollapse(cardId)}
                className="flex items-center justify-between px-lg py-md bg-transparent border-none cursor-pointer w-full"
              >
                <div className="text-black font-sans text-[18px] font-medium leading-normal">
                  {config.label}
                </div>
                {collapseIcon(!isCollapsed)}
              </button>

              {/* Card body */}
              {!isCollapsed && (
                isSpecifiedIllness
                  ? renderSpecifiedIllnessBody(card, index, config.cardsKey)
                  : renderDefaultBody(config, card, index)
              )}
            </div>
          );
        })}

        {/* Add button */}
        <button
          type="button"
          onClick={() => addCard(config.cardsKey)}
          className="flex items-center gap-sm cursor-pointer bg-transparent border-none p-0 w-fit"
        >
          {addIcon}
          <div className="text-purple-primary font-sans text-body-02 font-medium leading-normal capitalize">
            Add Another {config.label}
          </div>
        </button>
      </div>
    );
  };

  // For stepValid: dynamically check all required fields in active cards
  // This is handled by the wizard's STEP_FIELD_DEFINITIONS + custom stepValid logic

  return (
    <div className="flex flex-col gap-lg">
      {/* Title */}
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Other associated benefits
      </div>

      {/* Does the policyholder have any attaching protection benefits? */}
      <div className="flex flex-col gap-sm">
        <div className={labelClass}>
          Does the policyholder/ beneficiary have any attaching protection benefits? <span className="text-[#E7000B]">*</span>
        </div>
        <div className="relative">
          <select
            value={(data.hasProtectionBenefits as string) || ''}
            onChange={(e) => onFieldChange('hasProtectionBenefits', e.target.value)}
            className={selectClass(!!(data.hasProtectionBenefits as string))}
          >
            <option value="" disabled className="text-[#B6B6B6]">Choose an option</option>
            {YES_NO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} className="text-[#333]">{o.text}</option>
            ))}
          </select>
          {chevronIcon}
        </div>
        <div className="text-[#1E1E1E] font-sans text-body-03 font-normal leading-normal mt-[-4px]">
          (eg. Death in service, Executive Income Protection, Income Protection, Wage Protection, Specified Illness)
        </div>
      </div>

      {hasProtection && (
        <>
          {/* Checkboxes */}
          <div className="flex flex-col gap-sm" style={{ marginTop: '8px' }}>
            <div className={labelClass}>
              Which of these attaching protection benefits does the policyholder/beneficiary have?
            </div>
            <div className="flex flex-row items-center gap-xxl mt-sm">
              <button
                type="button"
                onClick={() => handleCheckboxToggle('lifeCoverEnabled', 'lifeCoverCards', !lifeCoverEnabled)}
                className="flex items-center gap-sm bg-transparent border-none p-0 cursor-pointer"
              >
                {checkboxIcon(lifeCoverEnabled)}
                <span className="text-black font-sans text-body-02 font-normal leading-normal">Life Cover</span>
              </button>
              <button
                type="button"
                onClick={() => handleCheckboxToggle('incomeProtectionEnabled', 'incomeProtectionCards', !incomeProtectionEnabled)}
                className="flex items-center gap-sm bg-transparent border-none p-0 cursor-pointer"
              >
                {checkboxIcon(incomeProtectionEnabled)}
                <span className="text-black font-sans text-body-02 font-normal leading-normal">Income Protection</span>
              </button>
              <button
                type="button"
                onClick={() => handleCheckboxToggle('specifiedIllnessEnabled', 'specifiedIllnessCards', !specifiedIllnessEnabled)}
                className="flex items-center gap-sm bg-transparent border-none p-0 cursor-pointer"
              >
                {checkboxIcon(specifiedIllnessEnabled)}
                <span className="text-black font-sans text-body-02 font-normal leading-normal">Specified Illness</span>
              </button>
            </div>
          </div>

          {/* Benefit sections */}
          {BENEFIT_CONFIG.map((config) => {
            const enabled = config.key === 'lifeCover' ? lifeCoverEnabled
              : config.key === 'incomeProtection' ? incomeProtectionEnabled
              : specifiedIllnessEnabled;

            if (!enabled) return null;
            return (
              <div key={config.key}>
                {renderBenefitCards(config)}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
